import React, { Children, useId, cloneElement, useRef } from "react";
import styled from "styled-components";
import { Icon } from "@material-ui/core";
import { alpha } from "@material-ui/core/styles/colorManipulator";
import useIsMobileDevice from "../../hooks/useIsMobileDevice";
import MUIButton from "@material-ui/core/Button";
import useTheme from "@material-ui/core/styles/useTheme";
import Button from "./Button";
import PropTypes from "prop-types";
import FocusTrap from "focus-trap-react";
import { useTranslation } from "react-i18next";

const getBreakPointWidth = (breakpoints, size) => {
  const idx = breakpoints.keys.indexOf(size);
  if (idx < 0) {
    return null;
  }
  const key = breakpoints.keys[idx + 1];

  if (!key) {
    return false;
  }
  return breakpoints.values[key];
};

const PopupBackground = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: ${(props) =>
    alpha(props.theme?.colors?.primary.dark.color || "#000000", 0.8)};
  z-index: 1101;
  backdrop-filter: blur(2px);
  display: flex;
  justify-content: center;
  align-items: center;
  inset: 0px;
`;

const PopupContainer = styled.div`
  background-color: white;
  color: black;
  position: relative;
  box-sizing: border-box;
  ${(props) => {
    console.log("The mui theme", props.theme);
    if (props.maxWidth) {
      const breakpoints = props?.theme?.MUITheme?.breakpoints || {
        keys: ["xs", "sm", "md", "lg", "xl"],
        values: {
          xs: 0,
          sm: 600,
          md: 900,
          lg: 1200,
          xl: 1536,
        },
      };
      const widthInPixels = getBreakPointWidth(breakpoints, props.maxWidth);
      if (widthInPixels) {
        console.log("We got max width", props.maxWidth, widthInPixels);
        return (
          "max-width: " +
          widthInPixels +
          "px;\n" +
          "max-height: calc(90vh - 60px);\n" +
          "margin: 16px\n"
        );
      }
    } else if (props.fullScreen) {
      console.log("Popup: Fullscreen");
      return `width: 100%;  
                height: 100%;                `;
    } else {
      console.log("Popup: If not, max height?");
      return "max-height: calc(90vh - 60px);\n" + "margin: 16px;\n";
    }
  }}
  ${(props) => {
    if (props.fullWidth) {
      console.log("Popup: Full width");
      return "width: 100% \n";
    }
  }}
  display: flex;
  flex-direction: column;
`;

PopupContainer.defaultProps = {
  maxWidth: "md",
};

const CloseButton = styled.button`
  position: absolute;
  right: 0;
  top: 0;
  background-color: white;
  padding: 10px;
  color: rgba(0, 0, 0, 0.7);
  cursor: pointer;
  border: none;
`;

const GoBackButton = styled(CloseButton)`
  right: auto;
  left: 0;
`;

let idCounter = 0;

const Popup = (props) => {
  const modalRef = useRef(null);
  const backgroundRef = useRef(null);
  const startClickTarget = useRef(null);
  const titleId = useRef(`popup-title-${++idCounter}`);
  const [t] = useTranslation();
  const onBackgroundMouseDown = (e) => {
    startClickTarget.current = e.target;
  };

  const onBackgroundMouseUp = (e) => {
    console.trace(
      "On background mouse up",
      e.currentTarget,
      e.target,
      modalRef.current,
    );
    // If we start the click inside the modal, don't close if mouse up is outside the modal
    if (modalRef.current.contains(startClickTarget.current)) {
      return;
    }
    // MuiSelect uses Portal, so the click will not be inside the dialog
    const isInMuiMenu = Boolean(e.target.closest(".MuiSelect-root"));
    if (isInMuiMenu) {
      return;
    }

    if (props.onClose) {
      props.onClose(e);
    }
  };

  if (!props.open) {
    return null;
  }
  let onClose = null;
  if (props.onClose) {
    onClose = (
      <CloseButton
        role="button"
        aria-label={t("Close dialog")}
        onClick={(e) => props.onClose()}
      >
        <Icon>close</Icon>
      </CloseButton>
    );
  }
  let onGoBack = null;
  if (props.onGoBack) {
    onGoBack = (
      <GoBackButton href onClick={(e) => props.onGoBack()}>
        <Icon>arrow_back</Icon>
      </GoBackButton>
    );
  }

  console.log("Popup on close", onClose);

  let hasTitle = false;
  // Add id to title field if present, so we can use aria labeled by
  const enhancedChildren = Children.map(props.children, (child) => {
    if (React.isValidElement(child) && child.type?.isPopupHeading) {
      hasTitle = true;
      const clonedElement = cloneElement(child, { id: titleId.current });
      console.log("Cloned element", clonedElement);
      return clonedElement;
    }
    return child;
  });

  return (
    <PopupBackground
      innerRef={backgroundRef}
      onMouseDown={onBackgroundMouseDown}
      onMouseUp={onBackgroundMouseUp}
    >
      <FocusTrap
        focusTrapOptions={{
          initialFocusTrap: hasTitle ? titleId.current : undefined,
          onDeactivate: props.onClose,
          escapeDeactivates: true,
          clickOutsideDeactivates: true,
        }}
      >
        <PopupContainer
          role="dialog"
          aria-modal="true"
          innerRef={modalRef}
          {...(hasTitle ? { "aria-labelledby": titleId.current } : {})}
          onClick={(e) => e.stopPropagation()}
          {...props}
        >
          {enhancedChildren}
          {onGoBack}
          {onClose}
        </PopupContainer>
      </FocusTrap>
    </PopupBackground>
  );
};

Popup.defaultProps = {
  open: true,
};

export const PopupHeadingContainer = styled.div`
  padding-top: 48px;
  padding-left: 30px;
  padding-right: 30px;
  padding-bottom: 30px;
`;

const StyledPopupHeading = styled.h2`
  /*margin-bottom: 16px; */
  font-size: 20px;
  text-align: center;
  font-weight: 500;
  margin: 0;
  color: ${(props) =>
    props.theme?.colors.primary.dark.color || "black"} !important;
`;

const PopupHeading = ({ children, id, subHeading }) => {
  return (
    <PopupHeadingContainer>
      <StyledPopupHeading id={id}>{children}</StyledPopupHeading>
      {subHeading && <PopupSubHeading>{subHeading}</PopupSubHeading>}
    </PopupHeadingContainer>
  );
};

PopupHeading.isPopupHeading = true;

PopupHeading.defaultProps = {
  subHeading: null,
  id: undefined,
};

PopupHeading.propTypes = {
  subHeading: PropTypes.string,
  id: PropTypes.string,
};

const PopupSubHeading = styled.h2`
  margin-top: 0;
  margin-bottom: 0;
  font-size: 16px;
  color: #595959;
  text-align: center;
`;

const PopupContent = styled.div`
  margin-bottom: 16px;
  flex-grow: 1;
  flex-shrink: 1;
  overflow: auto;
  padding: 16px 30px;
  font-weight: 400;
  p {
    margin-top: 0;
  }
`;

const PopupContentText = styled.div`
  margin-bottom: 16px;
  flex-grow: 1;
  flex-shrink: 1;
  overflow: auto;
  padding: 16px 30px;
  font-weight: 300;
  p {
    margin-top: 0;
  }
`;

const StyledPopupActions = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: ${(props) => {
    console.log("The props children", props.children);
    return Children.count(props.children) == 1 ? "flex-end" : "space-between";
  }};
  > button,
  a {
    margin-left: 8px;
  }

  padding: ${(props) => (props.isMobile ? "15px" : "40px")};
  padding-left: ${(props) => (props.isMobile ? "15px" : "64px")};
  padding-right: ${(props) => (props.isMobile ? "15px" : "64px")};
  /* Remove padding and spacing so they align with content */
  .MuiButton-text:first-child {
    margin-left: 0;
    padding-left: 0;
  }
  .MuiButton-text:last-child {
    margin-right: 0;
    padding-right: 0;
  }
`;

const PopupActions = (props) => {
  const isMobile = useIsMobileDevice();
  return <StyledPopupActions isMobile={isMobile} {...props} />;
};

const PopupTextContent = styled.div`
  margin-top: ${(props) => props.theme.MUITheme.spacing(4)}px;
  margin-bottom: ${(props) => props.theme.MUITheme.spacing(4)}px;
  text-align: center;
`;

const PopupButton = ({ secondary, ...props }) => {
  const theme = useTheme();

  const extraProps = {};
  if (theme.clientType == "desktop") {
    extraProps.variant = "primary";
    if (secondary) {
      extraProps.inverted = true;
    }
    return <Button {...extraProps} {...props} />;
  }
  extraProps.color = "primary";
  return <MUIButton {...extraProps} {...props} />;
};

PopupButton.defaultProps = {
  secondary: null,
};

PopupButton.propTypes = {
  secondary: PropTypes.string,
};

export {
  Popup,
  PopupHeading,
  PopupSubHeading,
  PopupActions,
  PopupContent,
  PopupTextContent,
  PopupButton,
};

export default Popup;
